import type { CanvasState, PendingConnect, SliceCreator } from '../types';
import { pickBestInPin, pickBestOutPin, pushHistory } from '../utils';
import type { SliceCtx } from '../ctx';

type SliceKeys =
  | 'openContextMenu'
  | 'closeContextMenu'
  | 'setPendingConnect'
  | 'clearPendingConnect'
  | 'openCommandBarAt'
  | 'closeCommandBar'
  | 'setCommandBarQuery'
  | 'openMapper'
  | 'closeMapper'
  | 'select'
  | 'deleteSelection'
  | 'beginConnect'
  | 'finishConnectAt'
  | 'markDraggingNodes'
  | 'consumePendingCenter';

export const createUISlice: SliceCreator<Pick<CanvasState, SliceKeys>> = (set, get) => {
  const resolveCtx = (): SliceCtx | undefined => (get as any).__ctx;
  const ensureCtx = () => {
    const ctx = resolveCtx();
    if (!ctx) throw new Error('canvas ctx missing for ui slice');
    return ctx;
  };

  return {
    openContextMenu: (x, y) =>
      set((state) => {
        state.ui.contextMenu = { open: true, x, y };
      }),
    closeContextMenu: () =>
      set((state) => {
        state.ui.contextMenu = { open: false, x: 0, y: 0 };
      }),
    setPendingConnect: (pending) =>
      set((state) => {
        state.ui.pendingConnect = pending;
      }),
    clearPendingConnect: () =>
      set((state) => {
        state.ui.pendingConnect = null;
      }),
    openCommandBarAt: (x, y, context) =>
      set((state) => {
        state.ui.commandBar = { ...state.ui.commandBar, open: true, x, y, context: context ?? null };
      }),
    closeCommandBar: () =>
      set((state) => {
        state.ui.commandBar = { ...state.ui.commandBar, open: false, query: '', context: null };
      }),
    setCommandBarQuery: (query) =>
      set((state) => {
        state.ui.commandBar.query = query;
      }),
    openMapper: (edgeId, view) =>
      set((state) => {
        state.ui.mapper = {
          open: true,
          edgeId,
          view: view ?? state.ui.mapper.view ?? 'liquid',
        };
      }),
    closeMapper: () =>
      set((state) => {
        state.ui.mapper = {
          ...state.ui.mapper,
          open: false,
          edgeId: null,
        };
      }),
    select: ({ nodes = [], edges = [] }) => {
      const currentSelection = get().ui.selection;
      const nextNodes = new Set(nodes);
      const nextEdges = new Set(edges);
      const sameNodes =
        currentSelection.nodes.size === nextNodes.size &&
        Array.from(nextNodes).every((id) => currentSelection.nodes.has(id));
      const sameEdges =
        currentSelection.edges.size === nextEdges.size &&
        Array.from(nextEdges).every((id) => currentSelection.edges.has(id));
      if (sameNodes && sameEdges) return;
      set((state) => {
        state.ui.selection.nodes = nextNodes;
        state.ui.selection.edges = nextEdges;
      });
    },
    deleteSelection: () => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        const nodeSelection = state.ui.selection.nodes;
        const edgeSelection = state.ui.selection.edges;
        if (nodeSelection.size === 0 && edgeSelection.size === 0) return;
        pushHistory(state);
        const prevNodeCount = state.graph.nodes.length;
        const prevEdgeCount = state.graph.edges.length;
        state.graph.nodes = state.graph.nodes.filter((node) => !nodeSelection.has(node.id));
        const removedNodeIds = new Set(Array.from(nodeSelection));
        state.graph.edges = state.graph.edges.filter(
          (edge) =>
            !edgeSelection.has(edge.id) &&
            !removedNodeIds.has(edge.source) &&
            !removedNodeIds.has(edge.target),
        );
        if (
          state.graph.nodes.length !== prevNodeCount ||
          state.graph.edges.length !== prevEdgeCount
        ) {
          bumpStructureVersion(state);
        }
        state.ui.selection = { nodes: new Set(), edges: new Set() };
      });
      queueInference({ immediate: true });
    },
    beginConnect: (nodeId, rawHandle, channel, io = 'out') =>
      set((state) => {
        const node = state.graph.nodes.find((n) => n.id === nodeId);
        let dataType: any = undefined;
        const kind: 'data' | 'exec' = channel || 'data';
        if (node && kind === 'data') {
          if (io === 'out') {
            const pins =
              ((node.data as any)?.pins?.data?.out as Array<{ id: string; type?: any }> | undefined) ??
              [];
            const pin = pins.find((p) => p.id === rawHandle);
            dataType = pin?.type;
          } else {
            const pins =
              ((node.data as any)?.pins?.data?.in as Array<{ id: string; type?: any }> | undefined) ??
              [];
            const pin = pins.find((p) => p.id === rawHandle);
            dataType = pin?.type;
          }
        }
        state.ui.pendingConnect = {
          nodeId,
          handleId: rawHandle,
          kind,
          io,
          dataType,
        } as PendingConnect;
      }),
    finishConnectAt: (x, y) => {
      const pending = get().ui.pendingConnect;
      if (!pending) return;
      set((state) => {
        state.ui.commandBar.open = true;
        state.ui.commandBar.context = { mode: 'connect', anchorX: x, anchorY: y };
        state.ui.commandBar.x = x;
        state.ui.commandBar.y = y;
      });
    },
    markDraggingNodes: (dragging) => {
      const { queueInference, cancelQueuedInference } = ensureCtx();
      set((state) => {
        state.ui.isDraggingNodes = dragging;
      });
      if (dragging) {
        cancelQueuedInference();
        return;
      }
      queueInference({ immediate: true });
    },
    consumePendingCenter: () => {
      const id = get().ui.pendingCenterNodeId;
      if (!id) return null;
      set((state) => {
        if (state.ui.pendingCenterNodeId === id) state.ui.pendingCenterNodeId = null;
      });
      return id;
    },
  };
};
